import React from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import InputField from "../../../../../common/components/formFields/inputField/inputField";
import Button from "../../../../../common/components/button/Button";
import {
  IRetailPosFormData,
  IRetailPosFormProps,
} from "../../../../../models/IRetailPosFormProps";
import styles from "./RetailPosForm.module.scss";
import callApi from "../../../../../utils/api-client";
import { ClickAwayListener } from "@mui/base/ClickAwayListener";

// Validation schema using Yup
const validationSchema = yup.object({
  retailBaseUrl: yup
    .string()
    .required("Retail POS base URL is required")
    .url("Please enter a valid URL")
    .matches(/^https?:\/\//, "URL must start with http:// or https://"),
  retailApiKey: yup
    .string()
    .required("API key is required")
    .min(10, "API key must be at least 10 characters"),
  retailSecretKey: yup
    .string()
    .required("Secret key is required")
    .min(10, "Secret key must be at least 10 characters"),
  retailCompanyName: yup
    .string()
    .required("Retail Company Name is required")
    .min(2, "Retail Company Name must be at least 2 characters")
    .max(100, "Retail Company Name must not exceed 100 characters"),
  businessSearchName: yup.string(),
  business: yup
    .object()
    .required("Select Business")
    .shape({
      id: yup.string().required("Business ID is required"),
      name: yup.string().required("Business name is required"),
    }),
});

function RetailPosForm({
  onSubmit,
  initialData,
  isLoading = false,
  disableBusinessSelection = false,
  actionButtonLabel = "Connect POS System",
  isUpdateMode = false,
  formUpdateState,
  editBtnCallback = () => {},
}: IRetailPosFormProps) {
  const [inputFocused, setInputFocused] = React.useState<boolean>(false);
  const [unlinkedBusinessList, setUnlinkedBusinessList] = React.useState<
    Array<{ id: string; name: string }>
  >([]);
  console.log(initialData, "initialData");
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
  } = useForm<IRetailPosFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: initialData || {
      retailBaseUrl: "",
      retailApiKey: "",
      retailSecretKey: "",
      retailCompanyName: "",
      business: {
        id: "",
        name: "",
      },
      businessSearchName: "",
    },
  });

  const handleReset = () => {
    reset();
  };

  const isFieldDisabled =
    isLoading || isSubmitting || (isUpdateMode && formUpdateState === "view");

  const onSubmitHandler = async (data: IRetailPosFormData) => {
    try {
      console.log("Submitting data:", data);

      const result = await onSubmit(data);
      if (result) {
        handleReset();
      }
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  const SearchBuyerByName = async (searchText: string) => {
    try {
      if (searchText.trim()) {
        const getUnlinkedBusinessListByName: any = await callApi<any>(
          `admin/pos/unlinked-business?q=${searchText}&page=1`,
          {
            method: "GET",
          },
          "getUnlinkedBusinessListByName"
        );
        const getUnlinkedBusinessListByNameData = getUnlinkedBusinessListByName;

        if (
          getUnlinkedBusinessListByNameData &&
          getUnlinkedBusinessListByNameData?.status === "SUCCESS"
        ) {
          const businessData = getUnlinkedBusinessListByNameData?.data;
          if (businessData && businessData?.length > 0) {
            const updatedBusiness = businessData?.map((element: any) => {
              return {
                id: element?.business_setup_id,
                name: element?.business_name,
              };
            });
            if (updatedBusiness?.length > 0) {
              setUnlinkedBusinessList(updatedBusiness);
            }
          } else if (businessData && businessData?.length === 0) {
            setUnlinkedBusinessList([]);
          }
        }
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
      } else {
        console.error("Fetch error:", error);
      }
    }
  };

  const onBusinessSearch = (searchText: string) => {
    setValue("businessSearchName", searchText);
    SearchBuyerByName(searchText);
  };

  // const handleReset = () => {
  //   reset();
  // };

  const onChangeHandler = (name: keyof IRetailPosFormData) => (text: any) => {
    setValue(name, text);
  };

  return (
    <div className={styles.formContainer}>
      <div className={styles.formHeader}>
        <h2 className={styles.title}>Retail POS Configuration</h2>
        <p className={styles.subtitle}>
          Configure your Retail POS system integration
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmitHandler)} className={styles.form}>
        <div className={styles.FormSections}>
          <div className={styles.businessSection}>
            {!isUpdateMode && (
              <div className={styles.formGroup}>
                <ClickAwayListener
                  onClickAway={() => {
                    setInputFocused(false);
                  }}
                >
                  <div className={`${styles.input} ${styles.productNameSugg}`}>
                    <InputField
                      value={watch("businessSearchName")}
                      placeholder="Search Business Name"
                      label="Search Business Name"
                      id="businessSearchName"
                      onChange={(e: any) => onBusinessSearch(e)}
                      register={register("businessSearchName")}
                      maxWidth="100%"
                      errorMessage={errors.businessSearchName?.message}
                      autoComplete={false}
                      onFocus={() => {
                        setInputFocused(true);
                      }}
                      tabIndex={1}
                      disabled={disableBusinessSelection || isFieldDisabled}
                    />
                    {inputFocused && unlinkedBusinessList?.length > 0 && (
                      <div className={styles.productNameSuggestionsContaine}>
                        {unlinkedBusinessList?.map(
                          (each_business: any, index: number) => (
                            <div
                              key={each_business.id}
                              onClick={() => {
                                setValue("business", {
                                  id: each_business.id,
                                  name: each_business.name,
                                });
                                setInputFocused(false);
                              }}
                              className={`${styles.productSuggestionItem} ${
                                index !== unlinkedBusinessList.length - 1
                                  ? styles.seperator
                                  : ""
                              }`}
                            >
                              {each_business.name}
                            </div>
                          )
                        )}
                      </div>
                    )}
                  </div>
                </ClickAwayListener>
              </div>
            )}
            <div className={styles.formGroup}>
              <InputField
                value={watch("business.name")}
                label="Business Name"
                placeholder="Selected Business Name"
                register={register("business.name")}
                errorMessage={errors.business?.name?.message}
                type="text"
                id="businessName"
                disabled={true}
              />
            </div>
          </div>

          <div className={styles.connectorContainer}>
            <div className={styles.connectorIcon}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17 8l4 4-4 4M7 16l-4-4 4-4M21 12H3"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>

          <div className={styles.posConfigSection}>
            <div className={styles.formGroup}>
              <InputField
                value={watch("retailBaseUrl")}
                label="Retail POS Base URL"
                placeholder="https://your-pos-system.com"
                register={register("retailBaseUrl")}
                errorMessage={errors.retailBaseUrl?.message}
                type="url"
                id="retailBaseUrl"
                disabled={isFieldDisabled}
                onChange={onChangeHandler("retailBaseUrl")}
              />
            </div>

            <div className={styles.formGroup}>
              <InputField
                value={watch("retailApiKey")}
                label="Retail API Key"
                placeholder="Enter your Retail API key"
                register={register("retailApiKey")}
                errorMessage={errors.retailApiKey?.message}
                type="password"
                id="retailApiKey"
                disabled={isFieldDisabled}
                onChange={onChangeHandler("retailApiKey")}
              />
            </div>

            <div className={styles.formGroup}>
              <InputField
                value={watch("retailSecretKey")}
                label="Retail Secret Key"
                placeholder="Enter your Retail secret key"
                register={register("retailSecretKey")}
                errorMessage={errors.retailSecretKey?.message}
                type="password"
                id="retailSecretKey"
                disabled={isFieldDisabled}
                onChange={onChangeHandler("retailSecretKey")}
              />
            </div>
            <div className={styles.formGroup}>
              <InputField
                value={watch("retailCompanyName")}
                label="Retail Company Name"
                placeholder="Enter your Retail company name"
                register={register("retailCompanyName")}
                errorMessage={errors.retailCompanyName?.message}
                type="text"
                id="retailCompanyName"
                disabled={isFieldDisabled}
                onChange={onChangeHandler("retailCompanyName")}
              />
            </div>
          </div>
        </div>

        <div className={styles.formActions}>
          <div className={styles.submitButtonContainer}>
            <Button
              type="submit"
              label={actionButtonLabel}
              handleClick={() => {}} // Required by ButtonProps interface
              disabled={isFieldDisabled}
              large={true}
              customClass={styles.submitButton}
            />
          </div>
          {isUpdateMode && initialData?.retailBaseUrl && (
            <div className={styles.submitButtonContainer}>
              <Button
                type="button"
                label={"Edit"}
                handleClick={editBtnCallback}
                large={true}
                customClass={styles.submitButton}
              />
            </div>
          )}
        </div>
      </form>
    </div>
  );
}

export default RetailPosForm;
